/**
 * ActionCountersContext - shares the "action center" counters across the app
 * (sidebar badges, header pill, dashboard widget). Auto-refreshes every 60s
 * and exposes a `refresh()` method to force a re-fetch after mutations.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../lib/api';

const ActionCountersContext = createContext(null);
const REFRESH_MS = 60_000;

export const ActionCountersProvider = ({ children, enabled = true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const r = await api.get('/dashboard/action-center');
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) { setLoading(false); return; }
    fetchData();
    const t = setInterval(fetchData, REFRESH_MS);
    return () => clearInterval(t);
  }, [enabled, fetchData]);

  const value = useMemo(() => ({
    loading,
    total: data?.total ?? 0,
    perRoute: data?.per_route ?? {},
    items: data?.items ?? [],
    currentMonthLabel: data?.current_month_label,
    refresh: fetchData,
  }), [data, loading, fetchData]);

  return (
    <ActionCountersContext.Provider value={value}>
      {children}
    </ActionCountersContext.Provider>
  );
};

export const useActionCounters = () => {
  const ctx = useContext(ActionCountersContext);
  // Safe no-op when the provider is missing (e.g. on tenant/owner portals)
  return ctx || { loading: true, total: 0, perRoute: {}, items: [], refresh: () => {} };
};
